"use client";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { useUser, useClerk } from "@clerk/nextjs";
import { EllipsisVertical, Pause, HelpCircle } from "lucide-react";
import { PauseSessionDialog } from "./pause-session-dialog";
import { useState } from "react";

interface InterviewMenuProps {
  onPauseSession?: () => void;
  onHelp?: () => void;
  releaseWakeLock?: () => void;
}

export function InterviewMenu({
  onPauseSession,
  onHelp,
  releaseWakeLock,
}: InterviewMenuProps) {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const [showPauseDialog, setShowPauseDialog] = useState(false);

  if (!user) {
    return null;
  }

  const handlePauseSession = () => {
    setShowPauseDialog(true);
    onPauseSession?.();
  };

  const handleHelp = () => {
    onHelp?.();
  };

  const handleSignOut = () => {
    // Release wake lock before signing out
    releaseWakeLock?.();
    // Redirect to home page after sign out
    signOut({ redirectUrl: "/" });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          {/* User Info */}
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center space-x-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.imageUrl} alt={user.fullName ?? ""} />
                <AvatarFallback>
                  {user.fullName?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user.fullName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user.primaryEmailAddress?.emailAddress}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          {/* Account Actions */}
          <DropdownMenuItem onClick={() => openUserProfile()}>
            Manage account
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut}>Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PauseSessionDialog
        open={showPauseDialog}
        onOpenChange={setShowPauseDialog}
      />
    </>
  );
}
